import type { AdminFlowWizardCardProps } from "../../models/admin-flow-cards.models";
import type { AdminFlowNode } from "../../models/admin-flows.models";
import { connectAdminFlowNodes } from "../../utils/admin-flows.utils";
import AdminFlowBlockSettings from "./AdminFlowBlockSettings";
import { useState } from "react";

// Edit one graph block without replacing branches created in the Whiteboard.
// Explicit upstream checkboxes retain merges when the same flow is presented as stacked cards.
const AdminFlowWizardCard = ({ flow, node, tools, position, onChange }: AdminFlowWizardCardProps) => {
  const [connectionError, setConnectionError] = useState("");
  const upstreamIds = flow.edges.filter((edge) => edge.to === node.id).map((edge) => edge.from);
  const candidates = flow.nodes.filter((candidate) => candidate.id !== node.id);

  const changeNode = (updated: AdminFlowNode) => {
    onChange({ ...flow, nodes: flow.nodes.map((entry) => (entry.id === updated.id ? updated : entry)) });
  };

  const deleteNode = () => {
    onChange({
      ...flow,
      nodes: flow.nodes.filter((entry) => entry.id !== node.id),
      edges: flow.edges.filter((edge) => edge.from !== node.id && edge.to !== node.id),
    });
  };

  const changeUpstream = (upstreamId: string, checked: boolean) => {
    setConnectionError("");

    if (!checked) {
      onChange({ ...flow, edges: flow.edges.filter((edge) => edge.from !== upstreamId || edge.to !== node.id) });

      return;
    }

    try {
      onChange(connectAdminFlowNodes(flow, upstreamId, node.id));
    } catch (error) {
      setConnectionError(error instanceof Error ? error.message : "These blocks cannot be connected.");
    }
  };

  return (
    <details
      key={node.id}
      open
      data-test={`admin-flow-wizard-card-${node.id}`}
      className="rounded border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
    >
      <summary className="cursor-pointer px-4 py-3 text-sm font-semibold">
        <span className="mr-2 text-green">{position + 1}.</span>
        {node.label || "Untitled block"}
        <span className="ml-2 text-xs font-normal capitalize text-gray-500 dark:text-gray-400">{node.kind}</span>
      </summary>
      <div className="border-t border-gray-200 p-4 dark:border-gray-700">
        <AdminFlowBlockSettings key={node.id} node={node} tools={tools} onChange={changeNode} onDelete={deleteNode} />
        {node.kind !== "source" && (
          <fieldset className="mt-4 border-t border-gray-200 pt-3 dark:border-gray-700">
            <legend className="px-1 text-xs font-semibold">Inputs from other blocks</legend>
            <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
              Select every input this step needs. Multiple inputs wait for all selected blocks.
            </p>
            {candidates.length === 0 && <p className="text-xs text-gray-500">Add another block to connect an input.</p>}
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              {candidates.map((candidate) => (
                <label key={candidate.id} className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    data-test={`admin-flow-wizard-input-${node.id}-${candidate.id}`}
                    checked={upstreamIds.includes(candidate.id)}
                    onChange={(event) => changeUpstream(candidate.id, event.target.checked)}
                    className="h-4 w-4 accent-green"
                  />
                  {candidate.label || "Untitled block"}
                </label>
              ))}
            </div>
          </fieldset>
        )}
        {connectionError && (
          <p
            role="alert"
            data-test={`admin-flow-wizard-connection-error-${node.id}`}
            className="mt-3 text-xs text-red-700 dark:text-red-300"
          >
            {connectionError}
          </p>
        )}
      </div>
    </details>
  );
};

export default AdminFlowWizardCard;
